// Single cart item card — thumbnail, qty +/-, inline note, inline discount.
// For serial-tracked lines: shows attached serial chips + a per-line scan input
// that resolves via the sell-context hook before attaching the serial ID.
import { useState, useRef, useCallback } from "react";
import { Trash2, Minus, Plus, StickyNote, Package, X, ScanLine, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/toast";
import type { OrderItem } from "./use-pos-cart";
import type { ScanResult, ScanSerialCandidate } from "@/features/inventory/use-scan-resolve";
import { SerialPickModal } from "@/features/inventory/serial-pick-modal";
import { useScannerAutoSubmit } from "@/features/inventory/use-scanner-auto-submit";

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0";
  return new Intl.NumberFormat("vi-VN").format(n);
}

interface CartItemCardProps {
  item: OrderItem & { imageUrl?: string };
  onUpdateQty: (qty: number) => void;
  onUpdateDiscount: (discount: string) => void;
  onUpdatePrice: (price: string) => void;
  onUpdateNote: (note: string) => void;
  onRemove: () => void;
  loading?: boolean;
  /** Được phép sửa đơn giá / chiết khấu. Khi false: chỉ hiển thị, không cho sửa. */
  canEditPrice?: boolean;
  /**
   * Resolver for serial-first scan — required when item.serialTracked is true.
   * Caller provides this from useScanResolve({ context:"sell", orderId }).
   * If absent the serial scan input is hidden even for tracked lines.
   */
  serialResolver?: (q: string) => Promise<ScanResult>;
  /** Called when a validated serial should be attached. Caller owns updateItem. */
  onAttachSerial?: (itemId: string, mergedSerialIds: string[]) => void;
  /** Called when a serial should be detached. Caller owns updateItem. */
  onDetachSerial?: (itemId: string, remainingSerialIds: string[]) => void;
}

// ── SerialArea — compact chip strip + scan input for serial-tracked lines ─────

interface SerialAreaProps {
  item: OrderItem;
  serialResolver: (q: string) => Promise<ScanResult>;
  onAttach: (itemId: string, mergedIds: string[]) => void;
  onDetach: (itemId: string, remainingIds: string[]) => void;
}

function SerialArea({ item, serialResolver, onAttach, onDetach }: SerialAreaProps) {
  const [scanValue, setScanValue] = useState("");
  const [scanning, setScanning] = useState(false);
  const [choices, setChoices] = useState<{ q: string; list: ScanSerialCandidate[] } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const attachedSerials = item.serials ?? [];
  const serialCount = attachedSerials.length;
  const needsMore = serialCount < item.quantity;
  const tooMany = serialCount > item.quantity;

  // Gắn 1 serial đã resolve vào dòng (dedup + merge đủ bộ vì updateItem REPLACE).
  const attachSerialId = useCallback(
    (serialId: string) => {
      const existingIds = attachedSerials.map((s) => s.id);
      if (existingIds.includes(serialId)) {
        toast.info("Serial đã gắn vào dòng này");
        return;
      }
      onAttach(item.id, [...existingIds, serialId]);
    },
    [attachedSerials, item.id, onAttach],
  );

  const submitScan = useCallback(
    async () => {
      if (!scanValue.trim() || scanning) return;
      const q = scanValue.trim();
      setScanning(true);
      try {
        const sr = await serialResolver(q);
        setScanValue("");

        // Alias trúng nhiều máy: chỉ giữ ứng viên ĐÚNG sản phẩm của dòng này.
        if (sr.kind === "serials") {
          const usable = sr.serials.filter((c) => c.ok && c.variant.id === item.variantId);
          if (usable.length === 0) {
            toast.error("Số này trùng nhiều máy nhưng không máy nào thuộc sản phẩm của dòng này — gõ IMEI đầy đủ");
            return;
          }
          if (usable.length === 1) {
            attachSerialId(usable[0].serial.id);
            return;
          }
          setChoices({ q, list: usable });
          return;
        }

        if (sr.kind !== "serial") {
          // variant/none result on a per-line scan means the code doesn't match a serial
          toast.error("Mã quét không phải serial hợp lệ cho sản phẩm này");
          return;
        }

        if (!sr.ok) {
          toast.error(sr.reason ?? "Serial không hợp lệ");
          return;
        }

        // Ensure the resolved serial belongs to this line's variant
        if (sr.variant.id !== item.variantId) {
          toast.error(
            `Serial thuộc sản phẩm khác (${sr.variant.productName} ${sr.variant.sku})`,
          );
          return;
        }

        attachSerialId(sr.serial.id);
      } finally {
        setScanning(false);
        setTimeout(() => inputRef.current?.focus(), 60);
      }
    },
    [scanValue, scanning, serialResolver, item.variantId, attachSerialId],
  );

  // Máy quét không gửi Enter → tự gắn IMEI khi vừa tít xong.
  useScannerAutoSubmit(scanValue, () => void submitScan(), { enabled: !scanning });

  const handleScanKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      void submitScan();
    },
    [submitScan],
  );

  const handleDetach = useCallback(
    (serialId: string) => {
      const remainingIds = attachedSerials
        .map((s) => s.id)
        .filter((id) => id !== serialId);
      onDetach(item.id, remainingIds);
    },
    [attachedSerials, item.id, onDetach],
  );

  return (
    <div className="mt-1.5 space-y-1">
      {/* Serial chips */}
      {attachedSerials.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {attachedSerials.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono"
            >
              {s.serialNo}
              <button
                type="button"
                aria-label={`Gỡ serial ${s.serialNo}`}
                className="hover:text-destructive transition-colors"
                onClick={() => handleDetach(s.id)}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Count indicator — dòng không serial-tracked thì không nhắc thiếu/thừa
          (bán không kèm IMEI vẫn hợp lệ), chỉ hiện số đã gắn khi có. */}
      {item.serialTracked ? (
        <div
          className={[
            "flex items-center gap-1 text-[10px]",
            tooMany
              ? "text-destructive"
              : needsMore
                ? "text-amber-600"
                : "text-green-600",
          ].join(" ")}
        >
          {(needsMore || tooMany) && <AlertCircle size={10} />}
          {serialCount}/{item.quantity} serial
          {needsMore && ` — còn thiếu ${item.quantity - serialCount}`}
          {tooMany && ` — quá ${serialCount - item.quantity}`}
        </div>
      ) : (
        serialCount > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
            {serialCount} serial đã gắn
          </div>
        )
      )}

      {/* Scan input (only show when more serials needed or to allow replacement) */}
      <div className="flex items-center gap-1">
        <ScanLine size={12} className="text-muted-foreground shrink-0" />
        <Input
          ref={inputRef}
          value={scanValue}
          onChange={(e) => setScanValue(e.target.value)}
          onKeyDown={handleScanKey}
          enterKeyHint="done"
          placeholder="Quét / gõ IMEI serial…"
          disabled={scanning}
          className="h-6 text-xs font-mono flex-1 min-w-0"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => void submitScan()}
          disabled={scanning || !scanValue.trim()}
          className="shrink-0 rounded bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground disabled:opacity-40"
        >
          Thêm
        </button>
      </div>

      {choices && (
        <SerialPickModal
          candidates={choices.list}
          query={choices.q}
          onPick={(c) => {
            setChoices(null);
            attachSerialId(c.serial.id);
            setTimeout(() => inputRef.current?.focus(), 60);
          }}
          onClose={() => setChoices(null)}
        />
      )}
    </div>
  );
}

// ── CartItemCard ──────────────────────────────────────────────────────────────

export function CartItemCard({
  item,
  onUpdateQty,
  onUpdateDiscount,
  onUpdatePrice,
  onUpdateNote,
  onRemove,
  loading,
  canEditPrice,
  serialResolver,
  onAttachSerial,
  onDetachSerial,
}: CartItemCardProps) {
  const [discountEditing, setDiscountEditing] = useState(false);
  const [discountDraft, setDiscountDraft] = useState(item.discount);
  const [priceEditing, setPriceEditing] = useState(false);
  const [priceDraft, setPriceDraft] = useState(item.unitPrice);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(item.note ?? "");

  const handleDiscountEdit = () => {
    setDiscountDraft(item.discount);
    setDiscountEditing(true);
  };

  const commitDiscount = () => {
    setDiscountEditing(false);
    const val = discountDraft.trim();
    if (val !== item.discount) onUpdateDiscount(val);
  };

  const handleDiscountKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commitDiscount();
    if (e.key === "Escape") setDiscountEditing(false);
  };

  const handlePriceEdit = () => {
    setPriceDraft(item.unitPrice);
    setPriceEditing(true);
  };

  const commitPrice = () => {
    setPriceEditing(false);
    const val = priceDraft.trim();
    if (val !== "" && val !== item.unitPrice) onUpdatePrice(val);
  };

  const handlePriceKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commitPrice();
    if (e.key === "Escape") setPriceEditing(false);
  };

  const handleNoteToggle = () => {
    if (!noteOpen) {
      setNoteDraft(item.note ?? "");
      setNoteOpen(true);
    } else {
      commitNote();
    }
  };

  const commitNote = () => {
    setNoteOpen(false);
    const val = noteDraft.trim();
    if (val !== (item.note ?? "")) onUpdateNote(val);
  };

  const handleNoteKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commitNote();
    if (e.key === "Escape") setNoteOpen(false);
  };

  // Hiện ô gõ IMEI cho MỌI dòng (không chỉ serialTracked): nhiều SP migrate từ
  // nhanh.vn thiếu cờ serial_tracked nhưng vẫn có IMEI thật trong kho → nhân
  // viên báo "phiếu bán lẻ không có chỗ gõ IMEI" (27/07). Dòng không tracked
  // chỉ khác là không bị nhắc thiếu/thừa serial.
  const showSerialArea = !!serialResolver && !!onAttachSerial && !!onDetachSerial;

  return (
    <div
      className={[
        "flex items-start gap-3 px-3 py-2 border-b hover:bg-muted/30 transition-colors",
        loading ? "opacity-50 pointer-events-none" : "",
      ].join(" ")}
    >
      {/* Thumbnail */}
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt=""
          className="h-10 w-10 md:h-12 md:w-12 rounded object-cover shrink-0 mt-0.5"
        />
      ) : (
        <div className="h-10 w-10 md:h-12 md:w-12 rounded bg-muted flex items-center justify-center shrink-0 mt-0.5">
          <Package size={20} className="text-muted-foreground" />
        </div>
      )}

      {/* Info + controls */}
      <div className="flex-1 min-w-0">
        {/* Row 1: name + qty + total + remove */}
        <div className="flex items-center gap-2">
          {/* Name */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{item.productName ?? "—"}</p>
            <p className="text-xs text-muted-foreground truncate">{item.sku}</p>
          </div>

          {/* Qty controls */}
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="h-7 w-7 p-0"
              disabled={item.quantity <= 1}
              onClick={() => onUpdateQty(item.quantity - 1)}
              aria-label="Giảm số lượng"
            >
              <Minus size={12} />
            </Button>
            <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 w-7 p-0"
              onClick={() => onUpdateQty(item.quantity + 1)}
              aria-label="Tăng số lượng"
            >
              <Plus size={12} />
            </Button>
          </div>

          {/* Line total */}
          <span className="font-semibold font-mono text-sm shrink-0 w-20 md:w-24 text-right">
            {fmtVND(item.lineTotal)} ₫
          </span>

          {/* Remove */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0"
            onClick={onRemove}
            aria-label="Xóa sản phẩm"
          >
            <Trash2 size={14} />
          </Button>
        </div>

        {/* Row 2: unit price + discount + note toggle */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
          {/* Unit price — editable only with permission */}
          {canEditPrice ? (
            priceEditing ? (
              <Input
                type="number"
                min={0}
                autoFocus
                className="w-28 h-6 text-xs text-right"
                value={priceDraft}
                onChange={(e) => setPriceDraft(e.target.value)}
                onBlur={commitPrice}
                onKeyDown={handlePriceKey}
              />
            ) : (
              <button
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={handlePriceEdit}
                aria-label="Chỉnh sửa đơn giá"
              >
                Giá: <span className="font-medium text-foreground">{fmtVND(item.unitPrice)} ₫</span>
              </button>
            )
          ) : (
            <span className="text-xs text-muted-foreground">Giá: {fmtVND(item.unitPrice)} ₫</span>
          )}

          {/* Discount — editable only with permission */}
          {canEditPrice ? (
            discountEditing ? (
              <Input
                type="number"
                min={0}
                autoFocus
                className="w-24 h-6 text-xs text-right"
                value={discountDraft}
                onChange={(e) => setDiscountDraft(e.target.value)}
                onBlur={commitDiscount}
                onKeyDown={handleDiscountKey}
              />
            ) : (
              <button
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleDiscountEdit}
                aria-label="Chỉnh sửa giảm giá"
              >
                Giảm: {fmtVND(item.discount)} ₫
              </button>
            )
          ) : (
            parseFloat(item.discount) > 0 && (
              <span className="text-xs text-muted-foreground">Giảm: {fmtVND(item.discount)} ₫</span>
            )
          )}

          {/* Note toggle */}
          <button
            className={[
              "flex items-center gap-1 text-xs transition-colors",
              noteOpen || item.note ? "text-amber-600" : "text-muted-foreground hover:text-foreground",
            ].join(" ")}
            onClick={handleNoteToggle}
            aria-label="Ghi chú sản phẩm"
          >
            <StickyNote size={12} />
            {!noteOpen && item.note ? (
              <span className="truncate max-w-[160px]">{item.note}</span>
            ) : (
              <span>Ghi chú</span>
            )}
          </button>
        </div>

        {/* Note input (inline, toggleable) */}
        {noteOpen && (
          <Input
            autoFocus
            className="mt-1 h-7 text-xs"
            placeholder="Màu đen, giao trước 5pm..."
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={commitNote}
            onKeyDown={handleNoteKey}
          />
        )}

        {/* Serial scan area — only for serial-tracked lines when resolver is wired */}
        {showSerialArea && (
          <SerialArea
            item={item}
            serialResolver={serialResolver}
            onAttach={onAttachSerial}
            onDetach={onDetachSerial}
          />
        )}
      </div>
    </div>
  );
}
